import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip as ChartTooltip,
  Legend,
  ChartOptions,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { useCreateBaseTransform } from '../../../hooks/climateAnalytics'
import { evaluateTransform } from './transformMath'
import EditableNumberField from './EditableNumberField'
import type {
  AffineShape,
  ConstantFunctionShape,
  OperationShape,
  PiecewiseConstantShape,
  PiecewiseLinearShape,
  SlotTransformSpec,
  Transform,
  TransformAxisSpec,
  TransformShape,
} from '../../../types/adaptations'

ChartJS.register(LinearScale, PointElement, LineElement, ChartTooltip, Legend)

const SHAPE_LABELS: Record<OperationShape, string> = {
  piecewise_linear: 'Linear curve',
  piecewise_constant: 'Stepped thresholds',
  affine: 'Linear formula',
  constant_function: 'Fixed value',
}

const SHAPE_HELP: Record<OperationShape, string> = {
  piecewise_linear: 'Interpolate smoothly between input and output points.',
  piecewise_constant: 'Change the output in discrete steps at defined thresholds.',
  affine: 'Calculate the output with slope × input + intercept.',
  constant_function: 'Return the same output for every input.',
}

type Point = { x: number; y: number }

const toDisplay = (value: number, axis: TransformAxisSpec) => value * axis.display_multiplier
const fromDisplay = (value: number, axis: TransformAxisSpec) => value / axis.display_multiplier
const axisTitle = (axis: TransformAxisSpec) => `${axis.label}${axis.unit ? ` (${axis.unit})` : ''}`

const finite = (value: number) => Number.isFinite(value)
const compactNumber = (value: number) => Number(value.toPrecision(6)).toLocaleString()

const sampleTransform = (transform: Transform, spec: SlotTransformSpec): Point[] => {
  const input = spec.input
  const samples = new Set<number>()
  const count = 160
  let sampleMin = input.display_min
  let sampleMax = input.display_max

  if (transform.shape_type === 'piecewise_linear' || transform.shape_type === 'piecewise_constant') {
    const displayKnots = (transform.shape as PiecewiseLinearShape | PiecewiseConstantShape).knots.map(knot =>
      toDisplay(knot, input),
    )
    sampleMin = Math.min(sampleMin, ...displayKnots)
    sampleMax = Math.max(sampleMax, ...displayKnots)
  }

  for (let i = 0; i <= count; i += 1) {
    samples.add(sampleMin + ((sampleMax - sampleMin) * i) / count)
  }

  if (transform.shape_type === 'piecewise_linear' || transform.shape_type === 'piecewise_constant') {
    const knots = (transform.shape as PiecewiseLinearShape | PiecewiseConstantShape).knots
    const epsilon = (sampleMax - sampleMin) / 100000
    knots.forEach(storageKnot => {
      const knot = toDisplay(storageKnot, input)
      samples.add(knot)
      if (transform.shape_type === 'piecewise_constant') {
        samples.add(knot - epsilon)
        samples.add(knot + epsilon)
      }
    })
  }

  return Array.from(samples)
    .sort((a, b) => a - b)
    .map(displayX => ({
      x: displayX,
      y: toDisplay(evaluateTransform(transform, fromDisplay(displayX, input)), spec.output),
    }))
    .filter(point => finite(point.y))
}

export function TransformChart({ transform, spec }: { transform: Transform; spec: SlotTransformSpec }) {
  const datasets = useMemo(() => {
    return [
      {
        label: transform.label || 'Draft impact function',
        data: sampleTransform(transform, spec),
        borderColor: '#1976d2',
        backgroundColor: '#1976d2',
        borderWidth: 2.5,
        pointRadius: 0,
      },
    ]
  }, [spec, transform])

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      interaction: { intersect: false, mode: 'nearest' },
      parsing: false,
      scales: {
        x: {
          type: 'linear',
          title: { display: true, text: axisTitle(spec.input) },
          suggestedMin: spec.input.display_min,
          suggestedMax: spec.input.display_max,
        },
        y: {
          type: 'linear',
          title: { display: true, text: axisTitle(spec.output) },
          suggestedMin: spec.output.display_min,
          suggestedMax: spec.output.display_max,
        },
      },
      plugins: { legend: { display: false } },
    }),
    [spec],
  )

  return (
    <Box sx={{ height: 300, minWidth: 0 }}>
      <Line data={{ datasets }} options={options} />
    </Box>
  )
}

function TransformTester({ transform, spec }: { transform: Transform; spec: SlotTransformSpec }) {
  const [displayInput, setDisplayInput] = useState((spec.input.display_min + spec.input.display_max) / 2)
  useEffect(() => {
    setDisplayInput((spec.input.display_min + spec.input.display_max) / 2)
  }, [spec])
  const storageOutput = evaluateTransform(transform, fromDisplay(displayInput, spec.input))
  const displayOutput = toDisplay(storageOutput, spec.output)
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
      <EditableNumberField
        size="small"
        label="Test an input"
        value={displayInput}
        onValueChange={setDisplayInput}
        helperText={axisTitle(spec.input)}
        sx={{ width: 240 }}
      />
      <Typography variant="body2" aria-live="polite">
        Output:{' '}
        <strong>
          {finite(displayOutput)
            ? displayOutput.toLocaleString(undefined, { maximumFractionDigits: spec.output.decimals })
            : 'outside the defined range'}
          {spec.output.unit ? ` ${spec.output.unit}` : ''}
        </strong>
      </Typography>
    </Stack>
  )
}

const shapeSummary = (transform: Transform, spec: SlotTransformSpec) => {
  switch (transform.shape_type) {
    case 'affine': {
      const shape = transform.shape as AffineShape
      const displaySlope = (shape.slope * spec.output.display_multiplier) / spec.input.display_multiplier
      const displayIntercept = shape.intercept * spec.output.display_multiplier
      return `Output = ${compactNumber(displaySlope)} × input ${displayIntercept < 0 ? '−' : '+'} ${compactNumber(
        Math.abs(displayIntercept),
      )}`
    }
    case 'constant_function':
      return `Always ${toDisplay((transform.shape as ConstantFunctionShape).value, spec.output).toLocaleString()}`
    case 'piecewise_linear':
      return `${(transform.shape as PiecewiseLinearShape).knots.length} points; values outside the points are clamped`
    case 'piecewise_constant': {
      const shape = transform.shape as PiecewiseConstantShape
      const thresholdRule =
        shape.side === 'left' ? 'a new value starts at its threshold' : 'a new value starts just after its threshold'
      return `${shape.knots.length} thresholds; ${thresholdRule}; values outside the thresholds use the nearest step`
    }
    default:
      return ''
  }
}

export function TransformPreviewDialog({
  open,
  onClose,
  transform,
  spec,
}: {
  open: boolean
  onClose: () => void
  transform: Transform | null
  spec: SlotTransformSpec
}) {
  if (!transform) return null
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{transform.label}</DialogTitle>
      <DialogContent>
        <ImpactFunctionVisualisation transform={transform} spec={spec} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export function ImpactFunctionVisualisation({ transform, spec }: { transform: Transform; spec: SlotTransformSpec }) {
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip size="small" label={SHAPE_LABELS[transform.shape_type]} />
        <Chip
          size="small"
          variant="outlined"
          label={transform.organisation_id == null ? 'Platform default' : 'Your organisation'}
        />
      </Stack>
      {transform.description && <Typography color="text.secondary">{transform.description}</Typography>}
      <Typography variant="body2">{shapeSummary(transform, spec)}</Typography>
      <TransformChart transform={transform} spec={spec} />
      <TransformTester transform={transform} spec={spec} />
    </Stack>
  )
}

const initialShape = (shapeType: OperationShape, spec: SlotTransformSpec): TransformShape => {
  const inputStart = fromDisplay(spec.input.display_min, spec.input)
  const inputEnd = fromDisplay(spec.input.display_max, spec.input)
  const outputStart = spec.output.min_value ?? fromDisplay(spec.output.display_min, spec.output)
  const outputEnd = spec.output.max_value ?? fromDisplay(spec.output.display_max, spec.output)

  switch (shapeType) {
    case 'piecewise_linear':
      return { knots: [inputStart, inputEnd], values: [outputStart, outputEnd], bounds: spec.bounds }
    case 'piecewise_constant':
      return { knots: [inputStart, inputEnd], values: [outputStart, outputEnd], side: spec.side }
    case 'affine': {
      const slope = inputEnd === inputStart ? 0 : (outputEnd - outputStart) / (inputEnd - inputStart)
      return { slope, intercept: outputStart - slope * inputStart }
    }
    case 'constant_function':
      return { value: outputStart }
  }
}

const draftTransform = (
  slotId: number,
  label: string,
  shapeType: OperationShape,
  shape: TransformShape,
): Transform => ({
  id: -slotId,
  organisation_id: -1,
  hazard_id: -1,
  exclusive_to: [slotId],
  slot_family: 'base',
  shape_type: shapeType,
  shape,
  label: label || 'Draft impact function',
})

const validateAxisValue = (value: number, axis: TransformAxisSpec, label: string): string | null => {
  if (!finite(value)) return `${label} must be a finite number.`
  const tolerance = 1e-12 * Math.max(1, Math.abs(value))
  if (axis.min_value != null && value < axis.min_value - tolerance) {
    return `${label} must be at least ${toDisplay(axis.min_value, axis)}${axis.unit ? ` ${axis.unit}` : ''}.`
  }
  if (axis.max_value != null && value > axis.max_value + tolerance) {
    return `${label} must be at most ${toDisplay(axis.max_value, axis)}${axis.unit ? ` ${axis.unit}` : ''}.`
  }
  return null
}

const validateDraft = (shapeType: OperationShape, shape: TransformShape, spec: SlotTransformSpec): string | null => {
  if (shapeType === 'piecewise_linear' || shapeType === 'piecewise_constant') {
    const piecewise = shape as PiecewiseLinearShape | PiecewiseConstantShape
    if (piecewise.knots.length < spec.min_points || piecewise.knots.length > spec.max_points) {
      return `Use between ${spec.min_points} and ${spec.max_points} points.`
    }
    if (piecewise.knots.length !== piecewise.values.length) return 'Every input must have an output.'
    for (let i = 0; i < piecewise.knots.length; i += 1) {
      const inputError = validateAxisValue(piecewise.knots[i], spec.input, `Input ${i + 1}`)
      if (inputError) return inputError
      const outputError = validateAxisValue(piecewise.values[i], spec.output, `Output ${i + 1}`)
      if (outputError) return outputError
      if (i > 0 && piecewise.knots[i] <= piecewise.knots[i - 1]) return 'Inputs must be strictly increasing.'
      if (spec.monotonic === 'non_decreasing' && i > 0 && piecewise.values[i] < piecewise.values[i - 1]) {
        return 'Outputs must not decrease.'
      }
    }
  }
  if (shapeType === 'affine') {
    const affine = shape as AffineShape
    if (!finite(affine.slope) || !finite(affine.intercept)) return 'Slope and intercept must be finite numbers.'
    if (spec.monotonic === 'non_decreasing' && affine.slope < 0) return 'Slope must not be negative.'
    const preview = draftTransform(0, '', shapeType, shape)
    for (const displayInput of [spec.input.display_min, spec.input.display_max]) {
      const output = evaluateTransform(preview, fromDisplay(displayInput, spec.input))
      const error = validateAxisValue(output, spec.output, `Output at ${displayInput}`)
      if (error) return error
    }
  }
  if (shapeType === 'constant_function') {
    return validateAxisValue((shape as ConstantFunctionShape).value, spec.output, 'Value')
  }
  return null
}

function PiecewiseEditor({
  shape,
  spec,
  onChange,
}: {
  shape: PiecewiseLinearShape | PiecewiseConstantShape
  spec: SlotTransformSpec
  onChange: (shape: PiecewiseLinearShape | PiecewiseConstantShape) => void
}) {
  const [paste, setPaste] = useState('')
  const [pasteOpen, setPasteOpen] = useState(false)
  const updatePoint = (index: number, field: 'knots' | 'values', displayValue: number) => {
    const values = [...shape[field]]
    values[index] = fromDisplay(displayValue, field === 'knots' ? spec.input : spec.output)
    onChange({ ...shape, [field]: values })
  }
  const removePoint = (index: number) => {
    onChange({
      ...shape,
      knots: shape.knots.filter((_, i) => i !== index),
      values: shape.values.filter((_, i) => i !== index),
    })
  }
  const addPoint = () => {
    const last = shape.knots.length - 1
    const step = last > 0 ? shape.knots[last] - shape.knots[last - 1] : 1 / spec.input.display_multiplier
    onChange({
      ...shape,
      knots: [...shape.knots, shape.knots[last] + Math.max(step, 1 / spec.input.display_multiplier)],
      values: [...shape.values, shape.values[last]],
    })
  }
  const replaceFromPaste = () => {
    const rows = paste
      .trim()
      .split(/\r?\n/)
      .map(row =>
        row
          .trim()
          .split(/[\t,;]/)
          .map(Number),
      )
      .filter(row => row.length >= 2 && row.every(finite))
    if (rows.length < spec.min_points) return
    onChange({
      ...shape,
      knots: rows.map(row => fromDisplay(row[0], spec.input)),
      values: rows.map(row => fromDisplay(row[1], spec.output)),
    })
    setPaste('')
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        Add points in ascending input order. Outputs must stay the same or increase.
      </Typography>
      <Box sx={{ maxHeight: 330, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{axisTitle(spec.input)}</TableCell>
              <TableCell>{axisTitle(spec.output)}</TableCell>
              <TableCell width={52} />
            </TableRow>
          </TableHead>
          <TableBody>
            {shape.knots.map((knot, index) => (
              <TableRow key={index}>
                <TableCell>
                  <EditableNumberField
                    size="small"
                    value={toDisplay(knot, spec.input)}
                    inputProps={{ 'aria-label': `${spec.input.label} point ${index + 1}` }}
                    onValueChange={value => updatePoint(index, 'knots', value)}
                  />
                </TableCell>
                <TableCell>
                  <EditableNumberField
                    size="small"
                    value={toDisplay(shape.values[index], spec.output)}
                    inputProps={{ 'aria-label': `${spec.output.label} point ${index + 1}` }}
                    onValueChange={value => updatePoint(index, 'values', value)}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title="Remove point">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => removePoint(index)}
                        disabled={shape.knots.length <= spec.min_points}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addPoint}
        disabled={shape.knots.length >= spec.max_points}
        sx={{ alignSelf: 'flex-start' }}
      >
        Add point
      </Button>
      <Button size="small" onClick={() => setPasteOpen(current => !current)} sx={{ alignSelf: 'flex-start' }}>
        {pasteOpen ? 'Hide bulk paste' : 'Paste multiple points'}
      </Button>
      {pasteOpen && (
        <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Stack spacing={1}>
            <TextField
              multiline
              minRows={3}
              value={paste}
              onChange={event => setPaste(event.target.value)}
              placeholder={`${spec.input.label}, ${spec.output.label}\n0, 0\n1, 10`}
              helperText="Paste two comma-, semicolon-, or tab-separated columns in the displayed units."
            />
            <Button
              variant="outlined"
              onClick={replaceFromPaste}
              disabled={!paste.trim()}
              sx={{ alignSelf: 'flex-start' }}
            >
              Replace points
            </Button>
          </Stack>
        </Box>
      )}
    </Stack>
  )
}

const apiErrorMessage = (error: unknown) =>
  (error as any)?.response?.data?.detail ?? (error as any)?.message ?? 'Could not create impact function.'

export function TransformEditorDialog({
  open,
  onClose,
  slot,
  templates = [],
  onCreated,
}: {
  open: boolean
  onClose: () => void
  slot: { id: number; label: string; transform_spec: SlotTransformSpec }
  templates?: Transform[]
  onCreated: (transform: Transform) => void
}) {
  const spec = slot.transform_spec
  const firstShape = spec.allowed_shapes[0]
  const [label, setLabel] = useState('')
  const [description, setDescription] = useState('')
  const [template, setTemplate] = useState<Transform | null>(null)
  const [shapeType, setShapeType] = useState<OperationShape>(firstShape)
  const [shape, setShape] = useState<TransformShape>(() => initialShape(firstShape, spec))
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    const nextShape = spec.allowed_shapes[0]
    setTemplate(null)
    setLabel('')
    setDescription('')
    setShapeType(nextShape)
    setShape(initialShape(nextShape, spec))
    setError(null)
  }, [open, spec])

  const platformTemplates = templates.filter(item => item.organisation_id == null)
  const organisationTemplates = templates.filter(item => item.organisation_id != null)

  const { mutate: createTransform, isLoading } = useCreateBaseTransform({
    onSuccess: transform => {
      onCreated(transform)
      onClose()
    },
    onError: requestError => setError(apiErrorMessage(requestError)),
  })

  const validationError = useMemo(() => validateDraft(shapeType, shape, spec), [shape, shapeType, spec])
  const draft = useMemo(() => draftTransform(slot.id, label, shapeType, shape), [label, shape, shapeType, slot.id])

  const changeShapeType = (next: OperationShape) => {
    setShapeType(next)
    setShape(
      template && template.shape_type === next ? JSON.parse(JSON.stringify(template.shape)) : initialShape(next, spec),
    )
  }

  const changeTemplate = (transformId: number | '') => {
    const next = transformId === '' ? null : templates.find(item => item.id === transformId) ?? null
    setTemplate(next)
    const nextShape = next?.shape_type ?? spec.allowed_shapes[0]
    setShapeType(nextShape)
    setShape(next ? JSON.parse(JSON.stringify(next.shape)) : initialShape(nextShape, spec))
    setLabel(next ? `Copy of ${next.label}` : '')
    setDescription(next?.description ?? '')
  }

  const save = () => {
    setError(null)
    if (!label.trim()) {
      setError('Name is required.')
      return
    }
    if (validationError) {
      setError(validationError)
      return
    }
    createTransform({
      slotId: slot.id,
      label: label.trim(),
      description: description.trim() || null,
      shapeType,
      params: shape,
      copiedFromTransformId: template?.id,
    })
  }

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Create impact function</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="subtitle2">{slot.label}</Typography>
            <Typography variant="body2" color="text.secondary">
              {axisTitle(spec.input)} → {axisTitle(spec.output)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              The new impact function will be available to your organisation in this slot only.
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Starting point
            </Typography>
            <FormControl fullWidth>
              <InputLabel id={`template-${slot.id}`}>Copy from</InputLabel>
              <Select
                labelId={`template-${slot.id}`}
                value={template?.id ?? ''}
                label="Copy from"
                onChange={event => changeTemplate(event.target.value === '' ? '' : Number(event.target.value))}
              >
                <MenuItem value="">Start from scratch</MenuItem>
                {organisationTemplates.length > 0 && <ListSubheader>Your organisation</ListSubheader>}
                {organisationTemplates.map(item => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.label}
                  </MenuItem>
                ))}
                {platformTemplates.length > 0 && <ListSubheader>Platform defaults</ListSubheader>}
                {platformTemplates.map(item => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Choose an existing impact function to copy, or start from scratch.</FormHelperText>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              Details
            </Typography>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Impact function name"
                  value={label}
                  onChange={event => setLabel(event.target.value)}
                  helperText="Shown when assigning the impact function to a property type."
                  fullWidth
                  required
                />
                <FormControl sx={{ minWidth: 280 }}>
                  <InputLabel id={`shape-${slot.id}`}>Function type</InputLabel>
                  <Select
                    labelId={`shape-${slot.id}`}
                    value={shapeType}
                    label="Function type"
                    onChange={event => changeShapeType(event.target.value as OperationShape)}
                  >
                    {spec.allowed_shapes.map(item => (
                      <MenuItem key={item} value={item}>
                        {SHAPE_LABELS[item]}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{SHAPE_HELP[shapeType]}</FormHelperText>
                </FormControl>
              </Stack>
              <TextField
                label="Description (optional)"
                value={description}
                onChange={event => setDescription(event.target.value)}
                helperText="Explain when this impact function should be used."
                fullWidth
                multiline
                minRows={2}
              />
            </Stack>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(340px, 0.9fr) minmax(400px, 1.1fr)' },
              gap: 3,
            }}
          >
            <Stack spacing={2}>
              <Typography variant="subtitle1" fontWeight={700}>
                Definition
              </Typography>
              {(shapeType === 'piecewise_linear' || shapeType === 'piecewise_constant') && (
                <PiecewiseEditor
                  shape={shape as PiecewiseLinearShape | PiecewiseConstantShape}
                  spec={spec}
                  onChange={next => setShape(next)}
                />
              )}
              {shapeType === 'affine' && (
                <Stack spacing={2}>
                  <EditableNumberField
                    label={`Slope (${axisTitle(spec.output)} per ${axisTitle(spec.input)})`}
                    value={
                      ((shape as AffineShape).slope * spec.output.display_multiplier) / spec.input.display_multiplier
                    }
                    onValueChange={value =>
                      setShape({
                        ...(shape as AffineShape),
                        slope: (value * spec.input.display_multiplier) / spec.output.display_multiplier,
                      })
                    }
                  />
                  <EditableNumberField
                    label={`Intercept (${spec.output.unit ?? spec.output.label})`}
                    value={toDisplay((shape as AffineShape).intercept, spec.output)}
                    onValueChange={value =>
                      setShape({
                        ...(shape as AffineShape),
                        intercept: fromDisplay(value, spec.output),
                      })
                    }
                  />
                </Stack>
              )}
              {shapeType === 'constant_function' && (
                <EditableNumberField
                  label={axisTitle(spec.output)}
                  value={toDisplay((shape as ConstantFunctionShape).value, spec.output)}
                  onValueChange={value => setShape({ value: fromDisplay(value, spec.output) })}
                />
              )}
              {shapeType === 'piecewise_linear' && (
                <Typography variant="caption" color="text.secondary">
                  Inputs below or above the points use the nearest endpoint value.
                </Typography>
              )}
              {shapeType === 'piecewise_constant' && (
                <Typography variant="caption" color="text.secondary">
                  {spec.side === 'left'
                    ? 'At a threshold, the function uses the new output value.'
                    : 'At a threshold, the function keeps the preceding output value; the new value starts just above it.'}
                </Typography>
              )}
              {validationError && <Alert severity="warning">{validationError}</Alert>}
            </Stack>

            <Stack spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Preview
              </Typography>
              <TransformChart transform={draft} spec={spec} />
              <TransformTester transform={draft} spec={spec} />
              {template && (
                <Typography variant="caption" color="text.secondary">
                  <ContentCopyIcon sx={{ fontSize: 14, verticalAlign: 'text-bottom', mr: 0.5 }} />
                  Started from “{template.label}”. Saving creates a new version; the source is unchanged.
                </Typography>
              )}
            </Stack>
          </Box>
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={isLoading || Boolean(validationError)}>
          {isLoading ? 'Creating…' : 'Create impact function'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
